
import React, { useState, useEffect, Suspense, lazy, Component, ReactNode } from 'react';
import { HashRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeContext } from './contexts/ThemeContext';
import { Navbar } from './components/Navbar';
import { PageTransition } from './components/PageTransition';
import { PageSkeleton } from './components/ui/PageSkeleton';
import { handleDeviceRedirect } from './utils/mobileCheck';

// 错误边界组件
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error?: Error }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Page Error:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-500 mb-4">页面加载失败</h2>
            <p className="text-slate-600 mb-4">{this.state.error?.message}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-500 text-white rounded-lg"
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Mobile Pages
const MobileHome = lazy(() => import('./pages/mobile/MobileHome'));
const MobileVideo = lazy(() => import('./pages/mobile/MobileVideo'));
const MobileDrawing = lazy(() => import('./pages/mobile/MobileDrawing'));
const MobileTaskCenter = lazy(() => import('./pages/mobile/MobileTaskCenter'));
const MobileProfile = lazy(() => import('./pages/mobile/MobileProfile'));

// Pages (lazy, use default exports from each file)
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VideoGenerate = lazy(() => import('./pages/VideoGenerate'));
const TaskCenter = lazy(() => import('./pages/TaskCenter'));
const TaskCenterSimple = lazy(() => import('./pages/TaskCenterSimple'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Help = lazy(() => import('./pages/Help'));
const CharacterCreate = lazy(() => import('./pages/CharacterCreate'));
const CharacterManage = lazy(() => import('./pages/CharacterManage'));
const SoraVeoCompare = lazy(() => import('./pages/SoraVeoCompare'));
const CrossborderDashboard = lazy(() => import('./pages/CrossborderDashboard'));
const EcommercePro = lazy(() => import('./pages/EcommercePro'));
const EcommerceWorkflows = lazy(() => import('./pages/EcommerceWorkflows'));
const RemixConsole = lazy(() => import('./pages/RemixConsole'));
const ViralClone = lazy(() => import('./pages/ViralClone'));
const TestGrokVideo = lazy(() => import('./pages/TestGrokVideo'));
const FFmpegTest = lazy(() => import('./pages/FFmpegTest'));
const FluxVideoPro = lazy(() => import('./pages/flux/FluxVideoPro'));
const SeedanceVideo = lazy(() => import('./pages/SeedanceVideo'));
const Drawing = lazy(() => import('./pages/Drawing'));

// TikTok Pages (lazy, use default exports from each file)
const TikTokHome = lazy(() => import('./pages/TikTokHome'));
const TikTokProducts = lazy(() => import('./pages/TikTokProducts'));
const TikTokCreators = lazy(() => import('./pages/TikTokCreators'));
const TikTokVideos = lazy(() => import('./pages/TikTokVideos'));
const TikTokShops = lazy(() => import('./pages/TikTokShops'));
const TikTokLives = lazy(() => import('./pages/TikTokLives'));
const TikTokCategories = lazy(() => import('./pages/TikTokCategories'));
const TikTokAds = lazy(() => import('./pages/TikTokAds'));
const TikTokCRM = lazy(() => import('./pages/TikTokCRM'));

// Admin Pages (lazy, use default exports from each file)
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./components/layouts/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminFinance = lazy(() => import('./pages/admin/AdminFinance'));
const AdminSystem = lazy(() => import('./pages/admin/AdminSystem'));
const AdminPrompts = lazy(() => import('./pages/admin/AdminPrompts'));
const EcomAdmin = lazy(() => import('./pages/admin/EcomAdmin'));

// Test Pages
const TestHome = lazy(() => import('./pages/TestHome'));
const TestAgent = lazy(() => import('./pages/TestAgent'));
const VideoAgentTest = lazy(() => import('./pages/VideoAgentTest'));
const GiopatoStyleHome = lazy(() => import('./pages/GiopatoStyleHome'));
const ADMStyleHome = lazy(() => import('./pages/ADMStyleHome'));


const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine if Navbar should be shown (Desktop Navbar)
  // Hide on Admin pages, Auth pages, Mobile pages, AND Test pages
  const isHiddenRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/m/') ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/test-home' ||
    location.pathname === '/test-agent' ||
    location.pathname === '/test-giopato' ||
    location.pathname === '/test-adm';

  // Device Detection & Redirect
  useEffect(() => {
    const redirectPath = handleDeviceRedirect(location.pathname);
    if (redirectPath) {
      console.log('Mobile device detected, redirecting to:', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [location.pathname]);

  return (
    <>
      {!isHiddenRoute && <Navbar />}
      {/* ✅ 为内容区域预留导航栏和安全区域的空间 */}
      <div className={`${!isHiddenRoute ? 'pt-[80px]' : ''}`}>
        <Suspense fallback={<PageSkeleton />}>
          <PageTransition>
            <Routes>
            <Route path="/" element={<VideoAgentTest />} />
            <Route path="/home" element={<Home />} />
            <Route path="/test-home" element={<TestHome />} />
            <Route path="/test-agent" element={<TestAgent />} />
            <Route path="/test-video-agent" element={<VideoAgentTest />} />
            <Route path="/test-giopato" element={<GiopatoStyleHome />} />
            <Route path="/test-adm" element={<ADMStyleHome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/generate" element={<VideoGenerate />} />
            <Route path="/drawing" element={<Drawing />} />
            <Route path="/characters/new" element={<CharacterCreate />} />
            <Route path="/characters" element={<CharacterManage />} />
            <Route path="/models/compare" element={<SoraVeoCompare />} />
            <Route path="/crossborder-dashboard" element={<CrossborderDashboard />} />
            <Route path="/ecommerce-pro" element={<EcommercePro />} />
            <Route path="/ecommerce-workflows" element={<EcommerceWorkflows />} />
            <Route path="/viral-clone" element={<ViralClone />} />
            <Route path="/remix-console" element={<RemixConsole />} />
            <Route path="/test-grok" element={<TestGrokVideo />} />
            <Route path="/flux-video" element={<FluxVideoPro />} />
            <Route path="/seedance" element={<SeedanceVideo />} />
            <Route path="/ffmpeg-test" element={<FFmpegTest />} />
            <Route path="/tasks" element={<ErrorBoundary><TaskCenter /></ErrorBoundary>} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/help" element={<Help />} />

            {/* Mobile Routes */}
            <Route path="/m/home" element={<MobileHome />} />
            <Route path="/m/video" element={<MobileVideo />} />
            <Route path="/m/drawing" element={<MobileDrawing />} />
            <Route path="/m/tasks" element={<MobileTaskCenter />} />
            <Route path="/m/profile" element={<MobileProfile />} />

            {/* TikTok Routes */}
            <Route path="/tiktok" element={<TikTokHome />} />
            <Route path="/tiktok/products" element={<TikTokProducts />} />
            <Route path="/tiktok/creators" element={<TikTokCreators />} />
            <Route path="/tiktok/videos" element={<TikTokVideos />} />
            <Route path="/tiktok/shops" element={<TikTokShops />} />
            <Route path="/tiktok/lives" element={<TikTokLives />} />
            <Route path="/tiktok/categories" element={<TikTokCategories />} />
            <Route path="/tiktok/ads" element={<TikTokAds />} />
            <Route path="/tiktok/crm" element={<TikTokCRM />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="prompts" element={<AdminPrompts />} />
              <Route path="ecom" element={<EcomAdmin />} />
              <Route path="system" element={<AdminSystem />} />
              <Route path="login" element={<AdminLogin />} />
            </Route>
            </Routes>
          </PageTransition>
        </Suspense>
      </div>
    </>
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    // Check system preference or localStorage
    const savedTheme = localStorage.getItem('tyak_theme') as 'light' | 'dark';
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('tyak_theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
          <AppContent />
        </ThemeContext.Provider>
      </AuthProvider>
    </Router>
  );
};

export default App;
